import type {CSSProperties, DragEventHandler, MouseEvent, MouseEventHandler, ReactNode, TouchEventHandler} from "react";
import {memo, useCallback, useContext} from "react";
import type {ValidIconName, ValidIconSize} from "../Svg/InlineSvg";
import {InlineSvg} from "../Svg/InlineSvg";
import {WorkingStateContext} from "../Tab/Lib/WorkingStateContext";
import {isTouchBased} from "../../GlobalStores/ProfileFromStorage";
import * as styles from "./StandardButton.module.scss";
import {useElevationClass} from "./Paper";

const translate = {
    primary: styles.buttonPrimary,
    secondary: styles.buttonSecondary,
    tertiary: styles.buttonTertiary,
    sidebar: styles.buttonSidebar,
    error: styles.buttonError,
    warning: styles.buttonWarning,
    success: styles.buttonSuccess,
    question: styles.buttonQuestion,
    info: styles.buttonInfo,
    default: styles.buttonDefault,
    transparent: styles.buttonTransparent
};

export type ColorSet = keyof typeof translate;

interface IProps {
    tabIndex?: number | undefined;
    iconName?: ValidIconName;
    iconSize?: "S" | "M" | "L" | "XL";
    iconRotate?: number;
    className?: string;
    color?: ColorSet;
    active?: boolean;
    disabled?: boolean;
    style?: CSSProperties;
    title?: string;
    circle?: boolean;
    onClick?: ((e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => Promise<unknown>) | undefined;
    onMouseOver?: MouseEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onMouseEnter?: MouseEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onMouseOut?: MouseEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onMouseDown?: MouseEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onTouchStart?: TouchEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onDragOver?: DragEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    onDrop?: DragEventHandler<HTMLDivElement | HTMLButtonElement> | undefined;
    children?: ReactNode;
    useDiv?: boolean;
    flat?: boolean;
    slim?: boolean;
}

export const StandardButton = memo(function StandardButton(props: IProps) {
    const ref = useContext(WorkingStateContext);
    const debounced = useCallback(
        (e: MouseEvent<HTMLDivElement | HTMLButtonElement>): void => {
            if (props.onClick) {
                if (ref.current) {
                    const [working, setWorking] = ref.current;
                    if (props.onClick && !working) {
                        setWorking(true);
                        props.onClick(e)?.finally(() => {
                            setWorking(false);
                        });
                    }
                } else {
                    props.onClick(e).catch(console.error);
                }
            }
        },
        [props, ref]
    );

    const utilClass = [translate[props.color || "secondary"]];

    if (props.className) {
        utilClass.push(props.className);
    }

    if (!isTouchBased() || props.color === "transparent") {
        utilClass.push(styles.desktop);
    }

    const elevationClassFlat = useElevationClass(0, true);
    const elevationClassFloating = useElevationClass(2, true);

    if (props.flat || props.color === "transparent") {
        // color = transparent = Workaround für den Moment, bis wir die Buttons überall umgestellt haben
        utilClass.push(elevationClassFlat);
    } else {
        utilClass.push(elevationClassFloating);
    }

    if (props.children) {
        utilClass.push(styles.buttonWithText);
    }

    if (props.circle) {
        utilClass.push(styles.circle);
    }

    if (props.active) {
        utilClass.push(styles.active);
    }

    if (props.slim || props.color === "transparent") {
        // color = transparent = Workaround für den Moment, bis wir die Buttons überall umgestellt haben
        utilClass.push(styles.slim);
    }

    const translateIconSize: {S: ValidIconSize; M: ValidIconSize; L: ValidIconSize; XL: ValidIconSize} = {
        S: 8,
        M: 16,
        L: 24,
        XL: 32
    };

    const content = (
        <>
            {props.iconName ? (
                <InlineSvg
                    rotate={props.iconRotate}
                    name={props.iconName}
                    size={translateIconSize[props.iconSize || "M"]}
                />
            ) : undefined}
            {props.children ? <div>{props.children}</div> : undefined}
        </>
    );

    const wrapperStyle = {
        tabIndex: props.tabIndex,
        style: props.style,
        disabled: props.disabled,
        className: utilClass.join(" "),
        onClick: debounced,
        onMouseDown: props.onMouseDown,
        onTouchStart: props.onTouchStart,
        onMouseEnter: props.onMouseEnter,
        onMouseOver: props.onMouseOver,
        onMouseOut: props.onMouseOut,
        onDragOver: props.onDragOver,
        onDrop: props.onDrop,
        title: props.title
    };

    let wrapper;
    if (props.useDiv) {
        if (wrapperStyle.disabled) {
            wrapperStyle.className += " " + styles.disabled;
        }

        delete wrapperStyle.disabled;

        wrapper = <div {...wrapperStyle}>{content}</div>;
    } else {
        wrapper = (
            <button type={"button"} {...wrapperStyle}>
                {content}
            </button>
        );
    }

    return wrapper;
});
